(function () {
  if (window.iGardenCart) return;
  let selectionLock = null;

  function assertCartAccess(options) {
    if (selectionLock && options.selectionLock !== selectionLock) {
      throw new Error('A selection checkout is already in progress.');
    }
  }

  function getCartComponent() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getShopifyRoot() {
    const root = String(window.Shopify?.routes?.root || '/');
    return root.endsWith('/') ? root : `${root}/`;
  }

  function getAjaxRoute(route, action) {
    const value = String(route || `${getShopifyRoot()}cart/${action}`);
    const match = value.match(/^([^?#]*)(.*)$/);
    const path = (match?.[1] || value).replace(/\/+$/, '');
    const suffix = match?.[2] || '';

    return `${path.endsWith('.js') ? path : `${path}.js`}${suffix}`;
  }

  async function requestCart(route, config, fallbackMessage) {
    const response = await fetch(route, config);
    const responseText = await response.text();
    let responseData = {};

    try {
      responseData = responseText ? JSON.parse(responseText) : {};
    } catch {
      responseData = {};
    }

    if (!response.ok || responseData.status) {
      const message = responseData.description || responseData.message || response.statusText || fallbackMessage;
      const error = new Error(message);
      error.status = response.status;
      error.response = responseData;
      throw error;
    }

    return responseData;
  }

  function getSectionIds(cart) {
    if (!cart || typeof cart.getSectionsToRender !== 'function') return [];

    try {
      return cart.getSectionsToRender().map((section) => section.id).filter(Boolean);
    } catch (error) {
      console.error('Unable to read cart sections', error);
      return [];
    }
  }

  function getAddedItem(responseData) {
    if (Array.isArray(responseData?.items) && responseData.items.length > 0) {
      return responseData.items[0];
    }

    return responseData || {};
  }

  function buildRenderData(responseData, finalCart) {
    if (!finalCart?.sections) return responseData;

    const addedItem = getAddedItem(responseData);

    return {
      ...finalCart,
      id: responseData?.id || addedItem.id,
      key: responseData?.key || addedItem.key,
      sections: finalCart.sections
    };
  }

  function normalizeItems(items) {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('At least one cart item is required.');
    }

    return items.map((item) => {
      const id = String(item.id || '').trim();
      const quantity = Number(item.quantity ?? 1);

      if (!id || !Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Invalid cart item.');
      }

      return { ...item, id, quantity };
    });
  }

  function cloneFormData(formData) {
    const clonedData = new FormData();
    formData.forEach((value, key) => clonedData.append(key, value));
    return clonedData;
  }

  function getFormItems(formData) {
    const items = [];
    const directId = String(formData.get('id') || '').trim();
    const directQuantity = Number(formData.get('quantity') || 1);

    if (directId && Number.isInteger(directQuantity) && directQuantity > 0) {
      items.push({ id: directId, quantity: directQuantity });
    }

    const accessoryIds = formData.getAll('items[][id]');
    const accessoryQuantities = formData.getAll('items[][quantity]');
    accessoryIds.forEach((id, index) => {
      const normalizedId = String(id || '').trim();
      const quantity = Number(accessoryQuantities[index] || 1);

      if (normalizedId && Number.isInteger(quantity) && quantity > 0) {
        items.push({ id: normalizedId, quantity });
      }
    });

    return items;
  }

  async function finalizeAdd(responseData, options = {}) {
    const cart = options.cart || getCartComponent();
    const sectionIds = getSectionIds(cart);
    const discountCodes = [...new Set(
      [
        ...(Array.isArray(options.discountCodes) ? options.discountCodes : []),
        options.discountCode
      ]
        .flatMap((code) => String(code || '').split(','))
        .map((code) => code.trim())
        .filter(Boolean)
    )];
    let finalCart = null;

    const applyDiscounts = window.iGardenDiscounts?.applyMany;
    const applyDiscount = window.iGardenDiscounts?.apply || window.iGardenApplyCartDiscount;
    if (discountCodes.length && typeof applyDiscounts === 'function') {
      finalCart = await applyDiscounts(discountCodes, {
        sections: sectionIds,
        sectionsUrl: options.sectionsUrl || window.location.pathname
      });
    } else if (discountCodes.length && typeof applyDiscount === 'function') {
      for (const code of discountCodes) {
        finalCart = await applyDiscount(code, {
          sections: sectionIds,
          sectionsUrl: options.sectionsUrl || window.location.pathname
        });
      }
    }

    if (!responseData.key && Array.isArray(responseData.items) && responseData.items[0]?.key) {
      responseData.key = responseData.items[0].key;
    }

    const renderData = buildRenderData(responseData, finalCart);
    let rendered = false;

    if (options.render !== false) {
      if (cart && renderData.sections && typeof cart.renderContents === 'function') {
        cart.renderContents(renderData);
        cart.classList.remove('is-empty');
        rendered = true;
      } else if (options.redirectToCart !== false) {
        window.location.assign(window.routes?.cart_url || '/cart');
      }
    }

    return {
      cartData: responseData,
      finalCart,
      renderData,
      rendered
    };
  }

  function dispatchCartAdded(items, responseData, finalization, options) {
    document.dispatchEvent(new CustomEvent('igarden:cart-added', {
      detail: {
        source: options.source || 'igarden-cart',
        action: options.action || 'add',
        items,
        cartData: responseData,
        finalCart: finalization.finalCart,
        finalized: true,
        rendered: finalization.rendered
      }
    }));
  }

  async function addPayload(payload, items, options = {}) {
    const cart = options.cart || getCartComponent();
    const sectionIds = getSectionIds(cart);
    const sectionsUrl = options.sectionsUrl || window.location.pathname;
    const isFormData = payload instanceof FormData;
    const requestPayload = isFormData ? cloneFormData(payload) : { ...payload };

    if (sectionIds.length > 0) {
      if (isFormData) {
        requestPayload.set('sections', sectionIds.join(','));
        requestPayload.set('sections_url', sectionsUrl);
      } else {
        requestPayload.sections = sectionIds;
        requestPayload.sections_url = sectionsUrl;
      }
    }

    if (cart && typeof cart.setActiveElement === 'function' && options.activeElement) {
      cart.setActiveElement(options.activeElement);
    }

    const headers = {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const responseData = await requestCart(
      getAjaxRoute(window.routes?.cart_add_url, 'add'),
      {
        method: 'POST',
        headers,
        body: isFormData ? requestPayload : JSON.stringify(requestPayload)
      },
      'Add to cart failed.'
    );

    const finalization = await finalizeAdd(responseData, {
      ...options,
      cart,
      sectionsUrl
    });

    dispatchCartAdded(items, responseData, finalization, options);
    return responseData;
  }

  async function addItems(items, options = {}) {
    assertCartAccess(options);
    const normalizedItems = normalizeItems(items);
    const payload = normalizedItems.length === 1
      ? { ...normalizedItems[0] }
      : { items: normalizedItems };

    return addPayload(payload, normalizedItems, options);
  }

  async function addForm(formOrData, options = {}) {
    assertCartAccess(options);
    let formData;

    if (formOrData instanceof HTMLFormElement) {
      formData = new FormData(formOrData);
    } else if (formOrData instanceof FormData) {
      formData = formOrData;
    } else {
      throw new Error('A product form or FormData instance is required.');
    }

    return addPayload(formData, getFormItems(formData), options);
  }

  async function clearCart(options = {}) {
    assertCartAccess(options);
    const cart = options.cart || getCartComponent();
    const sectionIds = getSectionIds(cart);
    const sectionsUrl = options.sectionsUrl || window.location.pathname;
    const payload = {};

    if (sectionIds.length > 0) {
      payload.sections = sectionIds;
      payload.sections_url = sectionsUrl;
    }

    const responseData = await requestCart(
      getAjaxRoute(window.routes?.cart_clear_url, 'clear'),
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      },
      'Clear cart failed.'
    );

    const resetDiscounts = window.iGardenDiscounts?.resetCart;
    const finalCart = typeof resetDiscounts === 'function'
      ? (await resetDiscounts(responseData, {
          sections: sectionIds,
          sectionsUrl,
          updateCart: options.updateDiscountsAfterClear !== false
        })) || responseData
      : responseData;
    let rendered = false;

    const canRenderDrawer = cart?.tagName === 'CART-DRAWER'
      && finalCart.sections
      && typeof cart.renderContents === 'function';

    if (options.render !== false && canRenderDrawer) {
      cart.renderContents(finalCart);
      cart.classList.add('is-empty');
      cart.querySelector('.drawer__inner')?.classList.add('is-empty');
      rendered = true;
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: options.source || 'igarden-cart',
        cartAction: 'clear',
        cartData: finalCart,
        rendered
      });
    }

    document.dispatchEvent(new CustomEvent('igarden:cart-cleared', {
      detail: {
        source: options.source || 'igarden-cart',
        cartData: finalCart,
        rendered
      }
    }));

    return finalCart;
  }

  async function buyNow(items, options = {}) {
    if (options.replaceCart === true) {
      await clearCart({
        ...options,
        render: false,
        updateDiscountsAfterClear: false,
        source: options.source || 'igarden-cart-buy-now'
      });
    }

    const responseData = await addItems(items, {
      ...options,
      action: 'buy-now',
      redirectToCart: false
    });

    window.location.assign(options.checkoutUrl || '/checkout');
    return responseData;
  }

  async function readCart() {
    const cart = await requestCart(`${getShopifyRoot()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }, 'Unable to read cart.');
    if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || !cart.currency) throw new Error('Invalid cart response.');
    return cart;
  }

  function selectionMatches(cart, items) {
    const expected = new Map();
    const actual = new Map();
    items.forEach((item) => expected.set(String(item.id), (expected.get(String(item.id)) || 0) + item.quantity));
    (cart.items || []).forEach((item) => actual.set(String(item.variant_id), (actual.get(String(item.variant_id)) || 0) + item.quantity));
    return expected.size === actual.size && [...expected].every(([id, quantity]) => actual.get(id) === quantity);
  }

  async function restoreSelectionCart(snapshot, options) {
    await clearCart(options);
    const items = (snapshot.items || []).map((item) => ({
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {},
      ...(item.selling_plan_allocation?.selling_plan?.id ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {})
    }));
    if (items.length) await addItems(items, options);
    const codes = (snapshot.discount_codes || []).map((entry) => typeof entry === 'string' ? entry : entry.code).filter(Boolean);
    if (codes.length && window.iGardenDiscounts?.applyMany) await window.iGardenDiscounts.applyMany(codes);
    else for (const code of codes) {
      if (window.iGardenDiscounts?.apply) await window.iGardenDiscounts.apply(code);
    }
    const restored = await readCart();
    if (!selectionMatches(restored, items)) throw new Error('Cart restoration was incomplete.');
    return restored;
  }

  async function checkoutSelection(items, options = {}) {
    assertCartAccess(options);
    const normalizedItems = normalizeItems(items);
    selectionLock = Symbol('selection-checkout');
    const cartOptions = {
      source: options.source || 'campaign-selection',
      selectionLock,
      render: false,
      redirectToCart: false,
      updateDiscountsAfterClear: false
    };
    let snapshot;
    let cartTouched = false;
    try {
      snapshot = await readCart();
      if (options.currency && snapshot.currency !== options.currency) throw new Error('Cart currency changed. Please reload.');
      if (options.beforeReplace && !(await options.beforeReplace(snapshot))) return null;
      const latestCart = await readCart();
      const fingerprint = (cart) => JSON.stringify((cart.items || []).map((item) => [item.key, item.quantity]));
      if (fingerprint(latestCart) !== fingerprint(snapshot)) throw new Error('The cart changed before checkout. Please try again.');
      cartTouched = true;
      await clearCart(cartOptions);
      await addItems(normalizedItems, cartOptions);
      const discountCodes = [...new Set(
        (Array.isArray(options.discountCodes) ? options.discountCodes : [])
          .flatMap((code) => String(code || '').split(','))
          .map((code) => code.trim())
          .filter(Boolean)
      )];
      const applyDiscounts = window.iGardenDiscounts?.applyMany;
      const applyDiscount = window.iGardenDiscounts?.apply || window.iGardenApplyCartDiscount;
      let finalCart = null;
      if (discountCodes.length && typeof applyDiscounts !== 'function' && typeof applyDiscount !== 'function') {
        throw new Error('Cart discount manager is unavailable.');
      }
      if (discountCodes.length && typeof applyDiscounts === 'function') {
        finalCart = await applyDiscounts(discountCodes);
        if (!finalCart) throw new Error('Unable to apply campaign discount codes.');
      } else {
        for (const code of discountCodes) {
          finalCart = await applyDiscount(code);
          if (!finalCart) throw new Error(`Unable to apply campaign discount code: ${code}`);
        }
      }
      if (!finalCart) finalCart = await readCart();
      window.location.assign(`${getShopifyRoot()}checkout`);
      return finalCart;
    } catch (error) {
      if (cartTouched && snapshot) {
        try { await restoreSelectionCart(snapshot, cartOptions); }
        catch (restoreError) { error.restoreFailed = true; error.restoreError = restoreError; }
      }
      throw error;
    } finally {
      selectionLock = null;
    }
  }

  window.iGardenCart = Object.freeze({
    addItem(item, options) {
      return addItems([item], options);
    },
    addItems,
    addForm,
    clear: clearCart,
    checkoutSelection,
    finalizeAdd,
    buyNow
  });
})();
